import React, { useState, useEffect } from "react";
import { useLocation, useNavigate, Outlet } from "react-router-dom";
import {
  AppBar,
  Toolbar,
  IconButton,
  Drawer,
  List,
  ListItemIcon,
  ListItemText,
  Divider,
  Avatar,
  Menu,
  MenuItem,
  Typography,
  Box,
  useTheme,
  styled,
  alpha,
  Tooltip,
  useMediaQuery,
  ListItemButton,
  Button,
  Chip,
  Fade,
  Zoom,
  Container,
  Badge,
  Stack,
} from "@mui/material";
import {
  Menu as MenuIcon,
  ChevronLeft,
  ChevronRight,
  Dashboard as DashboardIcon,
  MenuBook as RecipeIcon,
  AddCircle as CreateIcon,
  Inventory as InventoryIcon,
  History as HistoryIcon,
  Person as ProfileIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  RestartAlt as ResetIcon,
  Stop as StopIcon,
  WifiOff as DisconnectedIcon,
  Wifi as ConnectedIcon,
  Notifications as NotificationIcon,
  MoreVert as MoreIcon,
} from "@mui/icons-material";
import { GetMeProvider, useGetMe } from "../Context/GetMeContext";
import ThemeSwitcher from "../Components/ThemeSwitcher";
import {
  GetCookLaterRecipeProvider,
  useGetCookLaterRecipe,
} from "../Context/GetCookLaterRecipeContext";
import { KitchenDetailProvider } from "../Context/GetMyKitchenDetailContext";
import { MyInventoryProvider } from "../Context/MyInventoryContext";
import { useKitchenSocket } from "../Hooks/useKitchenSocket";
import FullscreenToggle from "../Components/FullScreenToggle";
import { KITCHEN_API_URL } from "../constants";
import { motion } from "framer-motion";

const drawerWidth = 240;
const collapsedWidth = 72;

const menuItems = [
  { label: "Dashboard", link: "dashboard", icon: <DashboardIcon /> },
  // { label: "Preloaded Recipes", link: "recipes", icon: <RecipeIcon /> },
  { label: "My Recipes", link: "my-recipes", icon: <CreateIcon /> },
  { label: "My Pantry", link: "inventory", icon: <InventoryIcon /> },
  { label: "Cooking History", link: "history", icon: <HistoryIcon /> },
  { label: "Settings", link: "settings", icon: <SettingsIcon /> },
  { label: "Profile", link: "profile", icon: <ProfileIcon /> },
];

// Animated components with framer-motion
const MotionBox = styled(motion.div)({
  display: "flex",
  width: "100%",
});

const MotionListItem = styled(motion.div)({
  width: "100%",
});

// Styled components
const Main = styled("main")(({ theme }) => ({
  flexGrow: 1,
  padding: 0,
  transition: theme.transitions.create(["margin", "background"], {
    easing: theme.transitions.easing.easeInOut,
    duration: theme.transitions.duration.standard,
  }),
  backgroundColor: theme.palette.background.default,
  minHeight: "100vh",
  marginLeft: 0,
  [theme.breakpoints.up("md")]: {
    marginLeft: collapsedWidth,
  },
}));

const StyledAppBar = styled(AppBar)(({ theme }) => ({
  width: "100%",
  boxShadow: "0 4px 20px 0 rgba(0,0,0,0.05)",
  backdropFilter: "blur(10px)",
  backgroundColor: alpha(theme.palette.background.paper, 0.8),
  color: theme.palette.text.primary,
  [theme.breakpoints.up("md")]: {
    width: `calc(100% - ${collapsedWidth}px)`,
    marginLeft: collapsedWidth,
  },
  transition: theme.transitions.create(
    ["width", "margin", "background-color"],
    {
      easing: theme.transitions.easing.easeInOut,
      duration: theme.transitions.duration.standard,
    }
  ),
}));

const StyledDrawer = styled(Drawer)(({ theme }) => ({
  "& .MuiDrawer-paper": {
    borderRight: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
    backgroundColor: theme.palette.background.paper,
    boxShadow:
      theme.palette.mode === "light"
        ? "4px 0px 20px 0 rgba(0,0,0,0.05)"
        : "4px 0px 20px 0 rgba(0,0,0,0.2)",
    overflow: "hidden",
    transition: theme.transitions.create(["width", "box-shadow"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
}));

const StyledListItemButton = styled(ListItemButton)(({ theme }) => ({
  margin: theme.spacing(0.5, 1),
  borderRadius: theme.shape.borderRadius,
  transition: theme.transitions.create(
    ["background-color", "transform", "box-shadow"],
    {
      duration: theme.transitions.duration.shorter,
    }
  ),
  "&.Mui-selected": {
    backgroundColor: alpha(theme.palette.primary.main, 0.12),
    "&:hover": {
      backgroundColor: alpha(theme.palette.primary.main, 0.18),
    },
    transform: "scale(1.02)",
    boxShadow: `0 2px 10px 0 ${alpha(theme.palette.primary.main, 0.2)}`,
  },
  "&:hover": {
    transform: "scale(1.01)",
  },
}));

const ActionButton = styled(Button)(({ theme, color = "primary" }) => ({
  borderRadius: theme.shape.borderRadius * 1.5,
  fontWeight: "bold",
  textTransform: "none",
  transition: theme.transitions.create(
    ["background-color", "box-shadow", "transform"],
    {
      duration: theme.transitions.duration.shorter,
    }
  ),
  boxShadow: theme.shadows[2],
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: theme.shadows[5],
  },
  "&:active": {
    transform: "translateY(0)",
  },
}));

export default function DashboardLayout() {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useGetMe();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isTablet = useMediaQuery(theme.breakpoints.down("lg"));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [hovering, setHovering] = useState(false);
  const [profileAnchor, setProfileAnchor] = useState(null);
  const [mobileMenuAnchor, setMobileMenuAnchor] = useState(null);
  const cookLaterRecipe = useGetCookLaterRecipe();

  // Initialize kitchen socket
  const { socket, emitWithTimeout, isConnected } =
    useKitchenSocket(KITCHEN_API_URL);

  // Handle emergency stop
  const handleEmergencyStop = async () => {
    try {
      await new Promise((resolve, reject) => {
        if (!socket?.connected) {
          return reject(new Error("Socket not connected"));
        }

        const timer = setTimeout(() => {
          reject(new Error("timeout"));
        }, 3000);

        socket.emit("abort", (response) => {
          clearTimeout(timer);
          resolve(response);
        });
      });
      console.log("Emergency stop signal sent");
    } catch (error) {
      console.error("Failed to send emergency stop:", error);
    }
  };

  // Handle reset
  const handleReset = async () => {
    try {
      await new Promise((resolve, reject) => {
        if (!socket?.connected) {
          return reject(new Error("Socket not connected"));
        }

        const timer = setTimeout(() => {
          reject(new Error("timeout"));
        }, 3000);

        socket.emit("reset", (response) => {
          clearTimeout(timer);
          resolve(response);
        });
      });
      console.log("Reset signal sent");
    } catch (error) {
      console.error("Failed to send reset:", error);
    }
  };

  // Handle user logout
  const handleLogout = () => {
    // Clear any stored auth tokens here if needed
    navigate("/logout");
  };

  console.log(user);

  useEffect(() => {
    setIsCollapsed(!isMobile);
  }, [isMobile]);

  const handleDrawerToggle = () => {
    if (isMobile) {
      setMobileOpen(!mobileOpen);
    } else {
      setIsCollapsed(!isCollapsed);
    }
  };

  const handleNavigation = (link) => {
    navigate(`/${link}`);
    if (isMobile) setMobileOpen(false);
  };

  const handleMobileMenuOpen = (event) => {
    setMobileMenuAnchor(event.currentTarget);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuAnchor(null);
  };

  const toggleDrawer = () => {
    setIsCollapsed(!isCollapsed);
  };

  // Animation variants
  const listItemVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: (i) => ({
      opacity: 1,
      x: 0,
      transition: {
        delay: i * 0.05,
        duration: 0.4,
        ease: "easeOut",
      },
    }),
  };

  // Connection status indicator
  const ConnectionStatus = () => (
    <Tooltip title={isConnected ? "Kitchen Connected" : "Kitchen Disconnected"}>
      <Chip
        icon={isConnected ? <ConnectedIcon /> : <DisconnectedIcon />}
        label={isConnected ? "Online" : "Offline"}
        color={isConnected ? "success" : "default"}
        size="small"
        sx={{
          fontWeight: "medium",
          mr: 1,
          "& .MuiChip-icon": {
            color: isConnected ? "inherit" : theme.palette.text.secondary,
          },
          transition: theme.transitions.create(
            ["background-color", "color", "box-shadow"],
            {
              duration: theme.transitions.duration.short,
            }
          ),
          ...(isConnected && {
            boxShadow: `0 0 8px ${alpha(theme.palette.success.main, 0.4)}`,
          }),
        }}
      />
    </Tooltip>
  );

  const drawingAnimationProps = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.4, ease: "easeOut" },
  };

  const drawerContent = (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <Box
        component={motion.div}
        {...drawingAnimationProps}
        sx={{
          p: 2,
          textAlign: "center",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Avatar
          src={user?.me?.profile_pic}
          sx={{
            width: isCollapsed ? 44 : 64,
            height: isCollapsed ? 44 : 64,
            mb: 2,
            transition: theme.transitions.create(["width", "height"], {
              duration: theme.transitions.duration.shorter,
            }),
            border: `2px solid ${theme.palette.primary.main}`,
            boxShadow: `0 0 15px ${alpha(theme.palette.primary.main, 0.3)}`,
          }}
        />
        {!isCollapsed && (
          <Fade in={!isCollapsed}>
            <Box>
              <Typography variant="subtitle1" fontWeight="bold">
                {user?.me?.name}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {user?.me?.email}
              </Typography>
            </Box>
          </Fade>
        )}
      </Box>
      <Divider sx={{ mb: 1 }} />

      <List sx={{ flexGrow: 1, overflow: "auto", px: 1 }}>
        {menuItems.map((item, index) => (
          <MotionListItem
            key={item.label}
            initial="hidden"
            animate="visible"
            custom={index}
            variants={listItemVariants}
          >
            <Tooltip
              title={isCollapsed ? item.label : ""}
              placement="right"
              arrow
            >
              <StyledListItemButton
                selected={location.pathname.includes(item.link)}
                onClick={() => handleNavigation(item.link)}
                sx={{
                  justifyContent: isCollapsed ? "center" : "initial",
                  py: 1.2,
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: isCollapsed ? 0 : 36,
                    color: location.pathname.includes(item.link)
                      ? theme.palette.primary.main
                      : "inherit",
                    mr: isCollapsed ? 0 : 1.5,
                    transition: theme.transitions.create("color", {
                      duration: theme.transitions.duration.standard,
                    }),
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                {!isCollapsed && (
                  <Fade in={!isCollapsed}>
                    <ListItemText
                      primary={item.label}
                      primaryTypographyProps={{
                        noWrap: true,
                        fontWeight: location.pathname.includes(item.link)
                          ? 600
                          : 400,
                      }}
                    />
                  </Fade>
                )}
              </StyledListItemButton>
            </Tooltip>
          </MotionListItem>
        ))}
        {/* Logout sidebar item */}
        <MotionListItem
          key="Logout"
          initial="hidden"
          animate="visible"
          custom={menuItems.length}
          variants={listItemVariants}
        >
          <Tooltip title={isCollapsed ? "Logout" : ""} placement="right" arrow>
            <StyledListItemButton
              onClick={handleLogout}
              selected={false}
              sx={{
                justifyContent: isCollapsed ? "center" : "initial",
                py: 1.2,
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: isCollapsed ? 0 : 36,
                  color: "inherit",
                  mr: isCollapsed ? 0 : 1.5,
                }}
              >
                <LogoutIcon />
              </ListItemIcon>
              {!isCollapsed && (
                <Fade in={!isCollapsed}>
                  <ListItemText primary="Logout" noWrap />
                </Fade>
              )}
            </StyledListItemButton>
          </Tooltip>
        </MotionListItem>
      </List>

      <Divider sx={{ my: 1 }} />
      <Box sx={{ p: 2, textAlign: "center" }}>
        {!isCollapsed && (
          <Fade in={!isCollapsed}>
            <Typography
              variant="body2"
              color="textSecondary"
              sx={{ fontSize: "0.75rem" }}
            >
              © {new Date().getFullYear()} AutoChef Robotic Kitchen
            </Typography>
          </Fade>
        )}
        <IconButton
          onClick={toggleDrawer}
          sx={{
            mt: isCollapsed ? 0 : 1,
            bgcolor: alpha(theme.palette.primary.main, 0.1),
            transition: theme.transitions.create("all", {
              duration: theme.transitions.duration.standard,
            }),
            "&:hover": {
              bgcolor: alpha(theme.palette.primary.main, 0.2),
              transform: "rotate(180deg)",
            },
          }}
        >
          {isCollapsed ? <ChevronRight /> : <ChevronLeft />}
        </IconButton>
      </Box>
    </Box>
  );

  // Mobile menu
  const renderMobileMenu = (
    <Menu
      anchorEl={mobileMenuAnchor}
      open={Boolean(mobileMenuAnchor)}
      onClose={handleMobileMenuClose}
      sx={{
        mt: 1,
      }}
      transformOrigin={{ horizontal: "right", vertical: "top" }}
      anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
    >
      <MenuItem onClick={handleReset} disabled={!isConnected}>
        <ListItemIcon>
          <ResetIcon
            fontSize="small"
            color={isConnected ? "info" : "disabled"}
          />
        </ListItemIcon>
        <ListItemText primary="Reset Arm" />
      </MenuItem>
      {/* <MenuItem
        onClick={() => {
          handleLogout();
          handleMobileMenuClose();
        }}
      >
        <ListItemIcon>
          <LogoutIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText primary="Logout" />
      </MenuItem> */}
    </Menu>
  );

  return (
    <Box sx={{ display: "flex" }}>
      <StyledAppBar
        position="fixed"
        sx={{
          [theme.breakpoints.up("md")]: {
            width: `calc(100% - ${
              isCollapsed ? collapsedWidth : drawerWidth
            }px)`,
            marginLeft: `${isCollapsed ? collapsedWidth : drawerWidth}px`,
          },
        }}
      >
        <Toolbar sx={{ justifyContent: "space-between" }}>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <IconButton
              color="inherit"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2, display: { md: "none" } }}
            >
              <MenuIcon />
            </IconButton>
            <Typography
              variant="h6"
              component={motion.div}
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, ease: "easeOut" }}
              sx={{
                fontWeight: 700,
                display: "flex",
                alignItems: "center",
                background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                textShadow: "0 0 5px rgba(0,0,0,0.1)",
              }}
            >
              AutoChef
            </Typography>
          </Box>

          {/* Desktop and Tablet View */}
          <Box
            component={motion.div}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.5 }}
            sx={{
              display: { xs: "none", sm: "flex" },
              alignItems: "center",
              gap: { sm: 1, md: 1.5 },
            }}
          >
            <Zoom in={true} style={{ transitionDelay: "100ms" }}>
              <Box>
                <ConnectionStatus />
              </Box>
            </Zoom>

            <Zoom in={true} style={{ transitionDelay: "200ms" }}>
              <Tooltip title="Emergency Stop">
                <ActionButton
                  variant="contained"
                  color="error"
                  onClick={handleEmergencyStop}
                  startIcon={<StopIcon />}
                  disabled={!isConnected}
                  size={isTablet ? "small" : "medium"}
                  sx={{
                    opacity: isConnected ? 1 : 0.7,
                    minWidth: isTablet ? "auto" : undefined,
                    px: isTablet ? 1 : 2,
                    ...(isConnected && {
                      animation: isConnected
                        ? `pulse 2s infinite ${theme.transitions.easing.easeInOut}`
                        : "none",
                      "@keyframes pulse": {
                        "0%": {
                          boxShadow: `0 0 0 0 ${alpha(
                            theme.palette.error.main,
                            0.7
                          )}`,
                        },
                        "70%": {
                          boxShadow: `0 0 0 5px ${alpha(
                            theme.palette.error.main,
                            0
                          )}`,
                        },
                        "100%": {
                          boxShadow: `0 0 0 0 ${alpha(
                            theme.palette.error.main,
                            0
                          )}`,
                        },
                      },
                    }),
                  }}
                >
                  {isTablet ? <StopIcon fontSize="small" /> : "STOP"}
                </ActionButton>
              </Tooltip>
            </Zoom>

            <Zoom in={true} style={{ transitionDelay: "300ms" }}>
              <Tooltip title="Reset Arm">
                <ActionButton
                  variant="contained"
                  color="info"
                  onClick={handleReset}
                  startIcon={!isTablet && <ResetIcon />}
                  disabled={!isConnected}
                  size={isTablet ? "small" : "medium"}
                  sx={{
                    opacity: isConnected ? 1 : 0.7,
                    minWidth: isTablet ? "auto" : undefined,
                    px: isTablet ? 1 : 2,
                  }}
                >
                  {isTablet ? <ResetIcon fontSize="small" /> : "Reset Arm"}
                </ActionButton>
              </Tooltip>
            </Zoom>

            <Zoom in={true} style={{ transitionDelay: "500ms" }}>
              <Box>
                <ThemeSwitcher />
              </Box>
            </Zoom>
            <Zoom in={true} style={{ transitionDelay: "400ms" }}>
              <Box>
                <FullscreenToggle />
              </Box>
            </Zoom>
            {/* <Zoom in={true} style={{ transitionDelay: "600ms" }}>
              <Tooltip title="Logout">
                <ActionButton
                  variant="contained"
                  color="secondary"
                  onClick={handleLogout}
                  startIcon={<LogoutIcon />}
                  size={isTablet ? "small" : "medium"}
                  sx={{ minWidth: isTablet ? "auto" : undefined }}
                >
                  {isTablet ? <LogoutIcon fontSize="small" /> : "Logout"}
                </ActionButton>
              </Tooltip>
            </Zoom> */}
          </Box>

          {/* Mobile View */}
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{ display: { xs: "flex", sm: "none" } }}
          >
            <ConnectionStatus />

            <Tooltip title="Emergency Stop">
              <span>
                {" "}
                {/* Wrapper to allow for disabled button in Tooltip */}
                <ActionButton
                  variant="contained"
                  color="error"
                  onClick={handleEmergencyStop}
                  disabled={!isConnected}
                  size="small"
                  sx={{
                    minWidth: 0,
                    width: 40,
                    height: 40,
                    p: 0,
                    opacity: isConnected ? 1 : 0.7,
                    ...(isConnected && {
                      animation: `pulse 2s infinite ${theme.transitions.easing.easeInOut}`,
                    }),
                  }}
                >
                  <StopIcon fontSize="small" />
                </ActionButton>
              </span>
            </Tooltip>

            <IconButton
              edge="end"
              color="inherit"
              onClick={handleMobileMenuOpen}
              sx={{ ml: 0.5 }}
            >
              <MoreIcon />
            </IconButton>
          </Stack>
        </Toolbar>
      </StyledAppBar>

      {renderMobileMenu}

      <Box
        component="nav"
        sx={{
          width: { md: isCollapsed ? 0 : 0 },
          flexShrink: { md: 0 },
        }}
      >
        <StyledDrawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: "block", md: "none" },
            "& .MuiDrawer-paper": {
              width: drawerWidth,
            },
          }}
        >
          {drawerContent}
        </StyledDrawer>

        <StyledDrawer
          variant="permanent"
          sx={{
            display: { xs: "none", md: "block" },
            "& .MuiDrawer-paper": {
              width: isCollapsed ? collapsedWidth : drawerWidth,
              overflowX: "hidden",
              transition: theme.transitions.create("width", {
                easing: theme.transitions.easing.easeInOut,
                duration: theme.transitions.duration.standard,
              }),
            },
          }}
          open
        >
          {drawerContent}
        </StyledDrawer>
      </Box>

      <Main
        sx={{
          [theme.breakpoints.up("md")]: {
            marginLeft: `${isCollapsed ? collapsedWidth : drawerWidth}px`,
            width: `calc(100% - ${
              isCollapsed ? collapsedWidth : drawerWidth
            }px)`,
          },
          transition: theme.transitions.create(["margin-left", "width"], {
            easing: theme.transitions.easing.easeInOut,
            duration: theme.transitions.duration.standard,
          }),
        }}
      >
        <Toolbar /> {/* Spacer for AppBar */}
        <Box
          component={motion.div}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.5 }}
        >
          <Outlet />
        </Box>
      </Main>
    </Box>
  );
}
